import React, { useEffect, useMemo, useState } from 'react'
import Panel from 'features/createBp/components/atoms/Panel'
import { useTranslation } from 'react-i18next'
import NextButton from 'features/createBp/components/atoms/NextButton'
import { useDispatch, useSelector } from 'react-redux'
import { getBuildingTypes } from 'features/bpList/ducks/selectors'
import { dissoc, propOr, values, equals } from 'ramda'
import BuildingTypeSelect from 'features/createBp/components/atoms/BuildingTypeSelect'
import { iterable } from 'utils/array'
import styled, { css } from 'styled-components'
import { isNotNil, isNotNilOrEmpty } from 'utils/ramda'
import { getCreatedBpId } from 'features/createBp/ducks/selectors'
import {
  createBpRoutine,
  updateBpRoutine
} from 'features/createBp/ducks/actions'
import InputDescription from 'features/createBp/components/atoms/InputDescription'

const BuildingInformation = ({
  completed,
  open,
  setActiveStep,
  setBp,
  setCompleted,
  bp
}) => {
  const { t } = useTranslation()
  const [sectionValues, setSectionValues] = useState(
    isNotNilOrEmpty(bp.buildingTypes)
      ? { ...bp.buildingTypes }
      : {
          0: {
            type: '',
            value:
              isNotNilOrEmpty(bp.uprn) || isNotNilOrEmpty(bp.postalCode)
                ? '0'
                : ''
          }
        }
  )
  const [manualTypeEditEnabled, setManualTypeEditEnabled] = useState(false)
  const [valid, setIsValid] = useState(false)
  const buildingTypes = useSelector(getBuildingTypes)
  const dispatch = useDispatch()
  const bpId = useSelector(getCreatedBpId)
  const resolvedBpId = bpId || bp.id
  const referenceClassificationCode =
    bp?.referenceData?.classificationCode || null
  const referenceClassificationDescription =
    bp?.referenceData?.classificationCodeDescription || null
  const suggestedBuildingType = bp?.suggestedBuildingType || null
  const normalizedSuggestedUnitsCount = Number.isInteger(
    Number(bp?.suggestedUnitsCount)
  )
    ? Number(bp.suggestedUnitsCount)
    : null
  const hasReferenceAutoselectedType = useMemo(() => {
    if (!referenceClassificationCode && !suggestedBuildingType) {
      return false
    }

    const firstType = values(sectionValues)[0]

    return (
      values(sectionValues).length === 1 && isNotNilOrEmpty(firstType?.type)
    )
  }, [referenceClassificationCode, suggestedBuildingType, sectionValues])
  const shouldLockAutofilledType =
    hasReferenceAutoselectedType && !manualTypeEditEnabled

  useEffect(() => {
    setManualTypeEditEnabled(false)
  }, [suggestedBuildingType, bp?.uprn])

  const handleOnChange = selectedType => {
    if (isNotNil(selectedType.index)) {
      setSectionValues(prev => ({
        ...prev,
        [selectedType.index]: {
          type: selectedType.type,
          value: selectedType.value,
          description: selectedType.description,
          units: selectedType.units,
          unit: selectedType.unit
        }
      }))
    }
  }

  const handleDelete = index => () => {
    setSectionValues(prev => ({ ...values(dissoc(index, prev)) }))
  }

  const addAnotherInput = () =>
    setSectionValues(prev => ({
      ...prev,
      [values(prev).length]: {
        type: '',
        value: hasUprnOrPostcode ? '0' : ''
      }
    }))

  const setNextSection = () => {
    setActiveStep('relationship')
    setCompleted('buildingDetails')
  }

  const buildTypeValues = () =>
    values(sectionValues).map(type => ({
      ...type,
      units:
        type.unit === 'sqm'
          ? Number(propOr(1, 'units', type))
          : Number(type.value),
      area: type.unit === 'sqm' ? Number(type.value) : null
    }))

  const handleSectionSubmit = () => {
    const normalizedFloorsNumber = Number.isInteger(Number(bp.floorsNumber))
      ? Number(bp.floorsNumber)
      : 0
    const normalizedUndergroundFloorsNumber = Number.isInteger(
      Number(bp.undergroundFloorsNumber)
    )
      ? Number(bp.undergroundFloorsNumber)
      : 0

    if (completed) {
      setNextSection()
    } else {
      const typeValues = buildTypeValues()
      setBp(prev => {
        if (isNotNilOrEmpty(resolvedBpId)) {
          dispatch(
            updateBpRoutine({
              buildingTypes: typeValues,
              floorsNumber: normalizedFloorsNumber,
              undergroundFloorsNumber: normalizedUndergroundFloorsNumber,
              usePublicDataUnitNames: Boolean(bp.usePublicDataUnitNames),
              id: resolvedBpId,
              isWizard: true
            })
          )
          setNextSection()
        } else {
          dispatch(
            createBpRoutine({
              ...bp,
              buildingTypes: typeValues,
              floorsNumber: normalizedFloorsNumber,
              undergroundFloorsNumber: normalizedUndergroundFloorsNumber,
              groundFloor: true,
              setNextSection
            })
          )
        }
        return {
          ...prev,
          floorsNumber: normalizedFloorsNumber,
          undergroundFloorsNumber: normalizedUndergroundFloorsNumber,
          buildingTypes: typeValues
        }
      })
    }
  }

  const hasUprnOrPostcode =
    isNotNilOrEmpty(bp.uprn) || isNotNilOrEmpty(bp.postalCode)

  useEffect(() => {
    const isValid =
      sectionValues &&
      values(sectionValues).every(type => {
        if (!isNotNilOrEmpty(type.type)) return false
        // With a selected address, the building type can be prefilled before
        // unit sync becomes available on the activated Building Passport.
        if (hasUprnOrPostcode) {
          return isNotNilOrEmpty(type.value) || Number(type.value) === 0
        }
        return isNotNilOrEmpty(type.value) && Number(type.value) > 0
      })
    setIsValid(isValid)
  }, [sectionValues, hasUprnOrPostcode])

  useEffect(() => {
    if (!open) {
      return
    }

    let nextSectionValues

    if (isNotNilOrEmpty(bp.buildingTypes)) {
      const parsedTypes = bp.buildingTypes.map(type => {
        const foundType = values(buildingTypes).find(bt => bt.key === type.type)
        const typeUnit = propOr('', 'unit', foundType)
        const fallbackUnitsValue =
          typeUnit === 'unit' &&
          normalizedSuggestedUnitsCount !== null &&
          Number(type.units) === 0
            ? normalizedSuggestedUnitsCount
            : type.units

        return {
          ...type,
          value: typeUnit === 'sqm' ? type.area : fallbackUnitsValue,
          units:
            typeUnit === 'sqm'
              ? normalizedSuggestedUnitsCount || type.units || 1
              : type.units
        }
      })

      if (parsedTypes[0]) {
        if (suggestedBuildingType) {
          parsedTypes[0].type = suggestedBuildingType
        }

        if (referenceClassificationDescription) {
          parsedTypes[0].description = referenceClassificationDescription
        }
      }

      nextSectionValues = { ...parsedTypes }
    } else if (suggestedBuildingType) {
      const suggestedTypeDefinition = values(buildingTypes).find(
        bt => bt.key === suggestedBuildingType
      )
      const suggestedTypeUnit = propOr('', 'unit', suggestedTypeDefinition)
      const suggestedTypeValue =
        suggestedTypeUnit === 'unit' && normalizedSuggestedUnitsCount !== null
          ? String(normalizedSuggestedUnitsCount)
          : hasUprnOrPostcode
            ? '0'
            : ''

      nextSectionValues = {
        0: {
          type: suggestedBuildingType,
          value: suggestedTypeValue,
          description: referenceClassificationDescription || '',
          units: normalizedSuggestedUnitsCount || 1
        }
      }
    } else {
      nextSectionValues = {
        0: { type: '', value: hasUprnOrPostcode ? '0' : '' }
      }
    }

    setSectionValues(prev =>
      equals(prev, nextSectionValues) ? prev : nextSectionValues
    )
  }, [
    open,
    bp,
    hasUprnOrPostcode,
    suggestedBuildingType,
    referenceClassificationDescription,
    buildingTypes,
    normalizedSuggestedUnitsCount
  ])

  const shouldBlockDwellingSole = useMemo(() => {
    const dwellingSoleUnit = values(sectionValues).filter(
      type => type.type === 'residential.dwelling.sole_unit'
    )
    return isNotNilOrEmpty(dwellingSoleUnit)
  }, [sectionValues])

  return (
    <Panel
      open={open}
      title='Building details'
      completed={completed}
      step='building-details'
      setActiveStep={setActiveStep}
      key={`panel-bp-details-${values(sectionValues).length}`}
    >
      {hasUprnOrPostcode && (
        <HintWrapper>
          <InputDescription margin='0 0 20px 0'>
            {hasReferenceAutoselectedType
              ? 'The main building type was selected automatically from the address reference data. If it does not look right, use Change to select it manually.'
              : 'Set the main building type here. Units can be synced after this Building Passport is activated.'}
          </InputDescription>
        </HintWrapper>
      )}
      {iterable(values(sectionValues).length).map((input, index) => {
        return (
          <BuildingTypeSelect
            value={propOr(null, index, sectionValues)}
            index={index}
            onChange={handleOnChange}
            suggestedUnitsCount={bp.suggestedUnitsCount}
            referenceClassificationDescription={
              index === 0 ? referenceClassificationDescription : ''
            }
            onUnlockTypeSelection={() => setManualTypeEditEnabled(true)}
            key={`building-type-input-${index + 1}`}
            id={`building-type-input-${index + 1}`}
            withDelete={
              values(sectionValues).length > 1 && !shouldLockAutofilledType
            }
            onDelete={handleDelete(index)}
            blockDwelling={values(sectionValues).length > 1}
            disabled={completed}
            lockTypeSelection={shouldLockAutofilledType}
            allowZeroUnits={hasUprnOrPostcode}
          />
        )
      })}
      <AddButton
        disabled={
          shouldBlockDwellingSole || completed || shouldLockAutofilledType
        }
        onClick={
          shouldBlockDwellingSole || completed || shouldLockAutofilledType
            ? () => {}
            : addAnotherInput
        }
      >
        + add another type
      </AddButton>
      <NextButton onClick={handleSectionSubmit} disabled={!valid}>
        {completed ? 'Save' : t('createBp.labels.next')}
      </NextButton>
    </Panel>
  )
}

export default BuildingInformation

const AddButton = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
  margin: 10px 0 20px auto;

  ${({ disabled, theme }) =>
    disabled &&
    css`
      color: ${theme.colors.grey[600]};
      cursor: default;
    `}
`

const HintWrapper = styled.div`
  margin-bottom: 4px;
`
